import React, { useEffect, useState, useCallback } from 'react';

/* ============================================================
   Youth Learner — persistent state, sound/haptic FX, context
   ============================================================ */

/* ---------------- Persistent gamification state ---------------- */

const STORAGE_KEY = 'assistivos_youth_v1';

const todayKey = () => new Date().toISOString().slice(0, 10);

const DEFAULT = {
  xp: 0,
  streakDays: 7,
  lastActive: todayKey(),
  heartsDay: todayKey(),
  hearts: 3,
  dailyDone: 0,
  dailyDate: todayKey(),
  courses: {},
  completedCourses: {},
  earnedBadges: {},
  bossResults: {},
};

export function useYouthState() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT, ...JSON.parse(raw) };
    } catch (_e) { /* ignore */ }
    return DEFAULT;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_e) { /* ignore */ }
  }, [state]);

  useEffect(() => {
    const t = todayKey();
    if (state.heartsDay !== t || state.dailyDate !== t) {
      setState((s) => ({ ...s, hearts: 3, heartsDay: t, dailyDone: 0, dailyDate: t }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => setState((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) })), []);
  return [state, update, setState];
}

/* ---------------- Sound & haptic settings ---------------- */

const FX_KEY = 'assistivos_youth_fx';
export const readFx = () => {
  try {
    const raw = localStorage.getItem(FX_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Backfill confetti default for existing users
      return { sound: true, haptic: true, confetti: true, ...parsed };
    }
  } catch (_e) { /* ignore */ }
  return { sound: true, haptic: true, confetti: true };
};
export const writeFx = (v) => { try { localStorage.setItem(FX_KEY, JSON.stringify(v)); } catch (_e) { /* ignore */ } };

export const playTone = (freq, dur = 120) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = freq;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur / 1000);
    o.start(); o.stop(ctx.currentTime + dur / 1000 + 0.05);
  } catch (_e) { /* ignore */ }
};
export const buzz = (ms) => { try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_e) { /* ignore */ } };

export const COURSE_SOUND_THEMES = {
  solar:    { correct: 660, wrong: 220, badge: [523, 659, 784] },
  robot:    { correct: 880, wrong: 196, badge: [523, 659, 880] },
  maker:    { correct: 587, wrong: 175, badge: [440, 587, 698] },
  cyber:    { correct: 784, wrong: 262, badge: [587, 740, 988] },
  spark:    { correct: 700, wrong: 233, badge: [494, 622, 831] },
  code:     { correct: 740, wrong: 208, badge: [554, 698, 932] },
  _default: { correct: 660, wrong: 220, badge: [523, 659, 784] },
};

export const fx = {
  correct: (settings, courseId) => {
    const t = COURSE_SOUND_THEMES[courseId] || COURSE_SOUND_THEMES._default;
    if (settings.sound) playTone(t.correct, 140);
    if (settings.haptic) buzz(30);
  },
  wrong: (settings, courseId) => {
    const t = COURSE_SOUND_THEMES[courseId] || COURSE_SOUND_THEMES._default;
    if (settings.sound) playTone(t.wrong, 200);
    if (settings.haptic) buzz([50, 40, 50]);
  },
  badge: (settings, courseId) => {
    const t = COURSE_SOUND_THEMES[courseId] || COURSE_SOUND_THEMES._default;
    if (settings.sound) {
      playTone(t.badge[0], 120);
      setTimeout(() => playTone(t.badge[1], 120), 130);
      setTimeout(() => playTone(t.badge[2], 200), 260);
    }
    if (settings.haptic) buzz([60, 40, 60]);
  },
  snap: (settings) => {
    if (settings.sound) playTone(1175, 55);
    if (settings.haptic) buzz(15);
  },
  matchComplete: (settings, courseId) => {
    const t = COURSE_SOUND_THEMES[courseId] || COURSE_SOUND_THEMES._default;
    if (settings.sound) {
      playTone(t.badge[0], 100);
      setTimeout(() => playTone(t.badge[2], 180), 115);
    }
    if (settings.haptic) buzz([35, 30, 35]);
  },
};

/* ---------------- Context ---------------- */

export const XPFloatContext = React.createContext(null);
export const useXP = () => React.useContext(XPFloatContext);

export const YouthContext = React.createContext(null);
export const useYouth = () => {
  const ctx = React.useContext(YouthContext);
  if (!ctx) throw new Error('useYouth must be used inside <YouthContext.Provider>');
  return ctx;
};

export const XPFloaterHost = ({ children }) => {
  const [floats, setFloats] = useState([]);
  const add = useCallback((amount, kind = 'xp') => {
    const id = Date.now() + Math.random();
    setFloats((f) => [...f, { id, amount, kind }]);
    setTimeout(() => setFloats((f) => f.filter((x) => x.id !== id)), 900);
  }, []);
  return (
    <XPFloatContext.Provider value={{ add }}>
      {children}
      <div className="absolute inset-0 pointer-events-none z-[120] overflow-hidden" aria-hidden="true">
        {floats.map((f) => (
          <div
            key={f.id}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-bold"
            style={{
              color: f.kind === 'heart' ? '#dc2626' : '#f97316',
              fontFamily: 'var(--p-font-mono)',
              fontSize: 20,
              animation: 'youth-xp-float 800ms ease-out forwards',
              textShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          >
            {f.kind === 'heart' ? '–1 ❤' : `+${f.amount} XP`}
          </div>
        ))}
      </div>
    </XPFloatContext.Provider>
  );
};
